import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import React from 'react';
import {
  useExportArchivVorgaengeCsv,
  useVorgaengeArchiv,
} from '@/adapter/opm-cockpit-backend/useVorgangApi';
import { ToniTaube } from '@/components/animation/LadeTaubeToni';
import { FilterSummary } from '@/components/filters/FilterSummary';
import { PaginationControls } from '@/components/ui/PaginationControls';
import { normalizeArchiveSearchFields } from '@/constants/archiveSearchFields';
import { ARCHIV_STATUS_FILTER_OPTIONS } from '@/constants/status';
import { useVorgangColumnSettings } from '@/hooks/useVorgangColumnSettings';
import { useArchivVorgangFilters } from '@/hooks/useVorgangFilters';
import { cn } from '@/lib/utils';
import { getColumns } from '@/pages/Vorgaenge/columns/getColumns';
import { ExportDropDownActions } from '@/pages/Vorgaenge/components/ExportDropDown/ExportDropDownActions';
import { SearchBar } from '@/pages/Vorgaenge/components/TableHeader/FullTextSearch';
import { getSortIcon } from '@/pages/Vorgaenge/components/TableHeader/SortIcon';
import {
  TableSettingsButton,
  TableSettingsModal,
} from '@/pages/Vorgaenge/components/TableSettingsModal';
import { COLUMN_FIELD_MAPPING } from '@/pages/Vorgaenge/mapping/columnFieldMapping';
import {
  VorgangTableClassNames,
  VorgangTableScrollContainer,
} from '@/pages/Vorgaenge/VorgangTable';
import type { VorgangListeItem } from '@/types';
import type { SearchOperator } from '@/types/filters';
import { ArchiveSearchScope } from './components/ArchiveSearchScope';
import { ArchiveWindowNotice } from './components/ArchiveWindowNotice';

interface ArchivVorgangTableProps {
  onEdit?: (item: VorgangListeItem) => void;
  onStateChange?: (state: {
    filters: ReturnType<typeof useArchivVorgangFilters>['filters'];
    pagination: PaginationState;
    sorting: SortingState;
  }) => void;
}

export const ArchivVorgangTable: React.FC<ArchivVorgangTableProps> = ({
  onEdit,
  onStateChange,
}) => {
  const {
    sorting,
    setSorting,
    columnOrder,
    setColumnOrder,
    fullColumnOrder,
    columnVisibility,
    setColumnVisibility,
    resetToDefault: resetColumnSettings,
  } = useVorgangColumnSettings();
  const [columnSettingsOpen, setColumnSettingsOpen] = React.useState(false);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const {
    filters,
    updateFilters,
    clearFilters,
    hasActiveFilters,
    archiveWindow,
    setArchiveWindow,
  } = useArchivVorgangFilters();
  const { exportCsv, isExporting } = useExportArchivVorgaengeCsv();

  const [searchInput, setSearchInput] = React.useState(
    filters.searchText || '',
  );
  const [searchOperator, setSearchOperator] = React.useState<SearchOperator>(
    filters.searchOperator || 'contains',
  );

  React.useEffect(() => {
    setSearchInput(filters.searchText || '');
  }, [filters.searchText]);

  React.useEffect(() => {
    setSearchOperator(filters.searchOperator || 'contains');
  }, [filters.searchOperator]);

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [filters, sorting]);

  React.useEffect(() => {
    onStateChange?.({ filters, pagination, sorting });
  }, [filters, pagination, sorting, onStateChange]);

  const sortArg =
    sorting.length > 0
      ? {
          sortBy: COLUMN_FIELD_MAPPING[sorting[0].id] || sorting[0].id,
          sortOrder: sorting[0].desc ? ('desc' as const) : ('asc' as const),
        }
      : undefined;

  const paginationArg = {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const {
    data: response,
    isLoading,
    error,
  } = useVorgaengeArchiv(filters, paginationArg, sortArg);
  const data = response?.data || [];

  const columns = getColumns({
    archiveFilters: true,
    // The implicit cutoff must not anchor the user's next calendar selection.
    createdAtFilterValue:
      archiveWindow === 'recent'
        ? { from: undefined, to: undefined }
        : filters.createdAt,
    filters,
    updateFilters,
    isAllPageSelected: false,
    isSomePageSelected: false,
    toggleSelectAll: () => undefined,
    toggleSelection: () => undefined,
    selectedIds: new Set<string>(),
    onEdit,
    selectable: false,
    user: null,
    statusOptions: ARCHIV_STATUS_FILTER_OPTIONS,
  });

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(fullColumnOrder) : updater;
      setColumnOrder(next.filter((id) => columnOrder.includes(id)));
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    renderFallbackValue: '-',
    manualPagination: true,
    manualSorting: true,
    pageCount: -1,
    state: {
      sorting,
      pagination,
      columnVisibility,
      columnOrder: fullColumnOrder,
    },
  });

  const handleRemoveFilter = (key: string) => {
    if (key === 'createdAt' || key === 'currentGestartedAm') {
      updateFilters({ [key]: { from: undefined, to: undefined } });
    } else if (key === 'searchText') {
      setSearchInput('');
      setSearchOperator('contains');
      updateFilters({ searchText: '', searchOperator: 'contains' });
    } else {
      updateFilters({ [key]: '' });
    }
  };

  const handleSearch = () =>
    updateFilters({ searchText: searchInput, searchOperator });
  const handleOperatorChange = (operator: SearchOperator) => {
    setSearchOperator(operator);
    if (searchInput.trim()) {
      updateFilters({ searchText: searchInput, searchOperator: operator });
    }
  };
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchOperator('contains');
    updateFilters({ searchText: '', searchOperator: 'contains' });
  };

  const buildExportRequest = (exportAll: boolean) => {
    const sortBy =
      sorting.length > 0
        ? COLUMN_FIELD_MAPPING[sorting[0].id] || sorting[0].id
        : undefined;
    const sortOrder = sorting[0]?.desc ? ('desc' as const) : ('asc' as const);

    return {
      filters,
      pagination: {
        page: exportAll ? 1 : pagination.pageIndex + 1,
        pageSize: exportAll ? 100_000 : pagination.pageSize,
      },
      sorting: sortBy && sortOrder ? { sortBy, sortOrder } : undefined,
      columns: [
        'id',
        'externerName',
        'currentStatus',
        'erstelltDatum',
        'currentGestartetAm',
        'auftragsnummer',
        'kundennummer',
        'vertragsnummer',
        'errorMessage',
        'errorStep',
        'lockedBy',
        'dokumentTyp',
        'unterdrueckung',
      ],
    };
  };

  const archiveNotice = (
    <ArchiveWindowNotice
      window={archiveWindow}
      range={filters.createdAt}
      onChange={setArchiveWindow}
    />
  );
  // The default date window has its own control; removing a custom date restores it.
  const summaryFilters =
    archiveWindow === 'recent'
      ? { ...filters, createdAt: { from: undefined, to: undefined } }
      : filters;
  const filterSummary = (
    <FilterSummary
      archiveFilters
      filters={summaryFilters}
      hasActiveFilters={hasActiveFilters}
      onRemoveFilter={handleRemoveFilter}
      onClearAllFilters={clearFilters}
    />
  );

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)] space-y-4">
      <div className="space-y-2">
        {archiveNotice}
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          operator={searchOperator}
          onOperatorChange={handleOperatorChange}
        >
          <ArchiveSearchScope
            fields={normalizeArchiveSearchFields(filters.searchFields)}
            onChange={(searchFields) => updateFilters({ searchFields })}
          />
        </SearchBar>
      </div>

      {filterSummary}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <ToniTaube width={300} height={300} />
        </div>
      ) : error ? (
        <div className="bg-white border border-table-border rounded-lg overflow-hidden p-8 text-center">
          <div className="text-red-600">Fehler beim Laden der Vorgänge</div>
        </div>
      ) : (
        <>
          <div className={VorgangTableClassNames.shell}>
            <VorgangTableScrollContainer>
              <table className="w-full">
                <thead className={VorgangTableClassNames.header}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            VorgangTableClassNames.headerCell,
                            header.id === 'select' && 'w-12',
                          )}
                        >
                          {header.isPlaceholder ? null : (
                            <div
                              className={`flex items-center justify-start ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <div className="flex items-center">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                              </div>
                              {header.column.getCanSort() && (
                                <div className="ml-1 flex items-center">
                                  {getSortIcon(header.column.getIsSorted())}
                                </div>
                              )}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 && hasActiveFilters ? (
                    <tr>
                      <td
                        colSpan={table.getVisibleLeafColumns().length}
                        className="p-8 text-center"
                      >
                        <div className="text-gray-600 text-lg font-medium">
                          Keine Einträge mit den gewählten Filtern gefunden
                        </div>
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-transparent transition-colors border-l-4 hover:border-primary hover:bg-table-hover"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="p-3 text-text-primary font-light text-sm"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </VorgangTableScrollContainer>
          </div>

          <PaginationControls
            currentPage={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            totalPages={null}
            totalCount={data.length}
            onPageChange={(page) => table.setPageIndex(page)}
            onPageSizeChange={(pageSize) => table.setPageSize(pageSize)}
            pageSizeOptions={[25, 50, 100]}
            children={
              <>
                <TableSettingsButton
                  onClick={() => setColumnSettingsOpen(true)}
                />
                <ExportDropDownActions
                  isExporting={isExporting}
                  exportScopeLabel="alle Treffer im gewählten Zeitraum (bis zu 100.000 Zeilen) exportieren"
                  onExportCurrentPage={async () =>
                    exportCsv(buildExportRequest(false))
                  }
                  onExportAll={async () => exportCsv(buildExportRequest(true))}
                />
              </>
            }
          />

          <TableSettingsModal
            open={columnSettingsOpen}
            onOpenChange={setColumnSettingsOpen}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onColumnOrderChange={setColumnOrder}
            onColumnVisibilityChange={setColumnVisibility}
            onResetToDefault={resetColumnSettings}
          />
        </>
      )}
    </div>
  );
};
